import {
  Call_Status,
  CallInfo,
  CallMember,
  CallMember_Status,
  CallMemberDevice,
  CallRecording_Type,
  CallUiConfiguration,
  CallUiConfiguration_ParticipantsControlType,
  CallUiConfiguration_ParticipantGridDisplayMode,
  CallUiConfiguration_PermissionsSectionDisplayMode,
  CallUiConfiguration_PreviewDisplayMode,
  CallMember_Role,
  CallUiConfiguration_MemberListDisplayMode,
  VideoQualitySettings,
  VideoResolution,
  Call_FinishReason,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/call/call_model_pb';
import {
  RealtimeCallEvent_CallChanged,
  RealtimeCallEvent_CallRecordingStarted,
  RealtimeCallEvent_CallRecordingStopped,
  RealtimeCallEvent_MemberChanged,
  RealtimeCallEvent_MemberForceFinished,
  RealtimeCallEvent_MemberForceMuted,
  RealtimeCallEvent_MemberPutHandDown,
  RealtimeCallEvent_MemberRaisedHand,
  RealtimeCallEvent_MemberRoleChanged,
  RealtimeCallEvent_MemberWasAdded,
  RealtimeCallEvent_MemberWasRemoved,
  RealtimeCallEvent_WaitingRoomChanged,
  RealtimeCallEvent_WaitingRoomMemberChanged,
  RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/call/call_realtime_events_pb';
import { WaitingRoomAccessRequest_Request } from '@gem/call/WsApi/unauthorizedWsCallsApi/types/call/call_requests_pb';

import {
  CallMemberStatus,
  CallStatus,
  CallVideoResolution,
  ICallInfo,
  ICallMember,
  ICallMemberChanged,
  ICallMemberDevice,
  ICallMemberForceFinished,
  ICallMemberForceMuted,
  ICallMemberHandAction,
  ICallMemberWasRemoved,
  ICallRecordingStarted,
  ICallRecordingStopped,
  ICallUIFeatures,
  IMemberRoleChanged,
  IStatusCallChanged,
  IVideoQualitySettings,
  IWaitingRoomChanged,
  IWaitingRoomMember,
  MemberListDisplayMode,
  ParticipantGridDisplayMode,
  ParticipantsControlType,
  PermissionsSectionDisplayMode,
  PreviewDisplayMode,
  RecordingType,
  StatusCallFinishReason,
  WaitingRoomAccessStatus,
  WaitingRoomMemberChangedStatus,
} from '../callModel.types';
import { CallMemberRole } from '../callPermissions/callPermissionsModel.types';

export const callUnauthorizedRecordingTypeResponseMap: Record<RecordingType, CallRecording_Type> = {
  [RecordingType.Audio]: CallRecording_Type.AUDIO,
  [RecordingType.AudioVideo]: CallRecording_Type.AUDIO_VIDEO,
};

export const callUnauthorizedB2bWaitingRoomMemberStatusResponseMap: Record<
  RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus,
  WaitingRoomMemberChangedStatus
> = {
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.INITIATED]:
    WaitingRoomMemberChangedStatus.Initiated,
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.APPROVED]:
    WaitingRoomMemberChangedStatus.Approved,
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.REJECTED]:
    WaitingRoomMemberChangedStatus.Rejected,
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.REQUESTED]:
    WaitingRoomMemberChangedStatus.Requested,
};

export const callUnauthorizedB2bWaitingRoomAccessStatusRequestMap: Record<
  WaitingRoomAccessStatus,
  WaitingRoomAccessRequest_Request
> = {
  [WaitingRoomAccessStatus.AskToJoin]: WaitingRoomAccessRequest_Request.ASK_TO_JOIN,
  [WaitingRoomAccessStatus.Cancel]: WaitingRoomAccessRequest_Request.CANCEL,
};

export const callUnauthorizedParticipantsControlTypeMap: Record<
  CallUiConfiguration_ParticipantsControlType,
  ParticipantsControlType
> = {
  [CallUiConfiguration_ParticipantsControlType.DISABLED]: ParticipantsControlType.Disabled,
  [CallUiConfiguration_ParticipantsControlType.TOTAL_NUMBER]: ParticipantsControlType.TotalNumber,
  [CallUiConfiguration_ParticipantsControlType.WITH_MAX_NUMBER]:
    ParticipantsControlType.WithMaxNumber,
};

export const callUnauthorizedParticipantGridDisplayModeMap: Record<
  CallUiConfiguration_ParticipantGridDisplayMode,
  ParticipantGridDisplayMode
> = {
  [CallUiConfiguration_ParticipantGridDisplayMode.ADMIN]: ParticipantGridDisplayMode.Admin,
  [CallUiConfiguration_ParticipantGridDisplayMode.ALL]: ParticipantGridDisplayMode.All,
};

export const callUnauthorizedPreviewDisplayModeMap: Record<
  CallUiConfiguration_PreviewDisplayMode,
  PreviewDisplayMode
> = {
  [CallUiConfiguration_PreviewDisplayMode.DEFAULT_PREVIEW]: PreviewDisplayMode.DefaultPreview,
  [CallUiConfiguration_PreviewDisplayMode.WEBINAR_PREVIEW]: PreviewDisplayMode.WebinarPreview,
};

export const callUnauthorizedPermissionsSectionDisplayModeMap: Record<
  CallUiConfiguration_PermissionsSectionDisplayMode,
  PermissionsSectionDisplayMode
> = {
  [CallUiConfiguration_PermissionsSectionDisplayMode.CUSTOM_PERMISSIONS]:
    PermissionsSectionDisplayMode.CustomPermissions,
  [CallUiConfiguration_PermissionsSectionDisplayMode.WEBINAR_SPEAKERS]:
    PermissionsSectionDisplayMode.WebinarSpeakers,
};

export const callUnauthorizedMemberListDisplayModeMap: Record<
  CallUiConfiguration_MemberListDisplayMode,
  MemberListDisplayMode
> = {
  [CallUiConfiguration_MemberListDisplayMode.DEFAULT]: MemberListDisplayMode.Default,
  [CallUiConfiguration_MemberListDisplayMode.NAME_WITH_ROLE]: MemberListDisplayMode.NameWithRole,
};

export function mapCallUnauthorizedMemberStatus(status: CallMember_Status): CallMemberStatus {
  switch (status) {
    case CallMember_Status.INITIATED:
      return CallMemberStatus.NotOnCall;
    case CallMember_Status.JOINED:
      return CallMemberStatus.OnCall;
    case CallMember_Status.DECLINED:
      return CallMemberStatus.Declined;
    case CallMember_Status.LEFT:
      return CallMemberStatus.Left;
    default:
      return CallMemberStatus.NotOnCall;
  }
}

export function mapCallUnauthorizedB2bMemberStatus(status: CallMember_Status): CallMemberStatus {
  switch (status) {
    case CallMember_Status.INITIATED:
      return CallMemberStatus.NotOnCall;
    case CallMember_Status.JOINED:
      return CallMemberStatus.OnCall;
    case CallMember_Status.DECLINED:
      return CallMemberStatus.Declined;
    case CallMember_Status.LEFT:
      return CallMemberStatus.Left;
    default:
      return CallMemberStatus.NotOnCall;
  }
}

export const mapCallUnauthorizedMemberDevice = (
  item: CallMemberDevice,
  status: CallMember_Status
): ICallMemberDevice => {
  return {
    deviceId: item.deviceId!,
    shareScreenUid: item.screenId,
    providerUserUid: item.cameraId,
    status: mapCallUnauthorizedB2bMemberStatus(status),
  };
};

export const mapCallUnauthorizedMember = (item: CallMember): ICallMember => {
  return {
    providerUserUid: item.device!.cameraId,
    shareScreenUid: item.device!.screenId,
    avatarUrl: item.displayData!.avatarUrl,
    hasPermissionRequest: false,
    isHandRaised: false,
    handRaisedAt: null,
    platform: undefined,
    status: mapCallUnauthorizedMemberStatus(item.status),
    userId: item.memberId.toString(),
    username: item.displayData!.userName,
    isAdmin: item.isAdmin,
    device: item.device?.deviceId
      ? mapCallUnauthorizedMemberDevice(item.device, item.status)
      : null,
  };
};

export const mapCallUnauthorizedMembers = (data: CallMember[]): ICallMember[] => {
  return data.map((item) => {
    return mapCallUnauthorizedMember(item);
  });
};

export const mapUnauthorizedCallUiFeatures = (data: CallUiConfiguration): ICallUIFeatures => {
  return {
    ...data,
    participantsControlType:
      callUnauthorizedParticipantsControlTypeMap[data.participantsControlType],
    participantGridDisplayMode:
      callUnauthorizedParticipantGridDisplayModeMap[data.participantGridDisplayMode],
    previewDisplayMode: callUnauthorizedPreviewDisplayModeMap[data.previewDisplayMode],
    permissionsSectionDisplayMode:
      callUnauthorizedPermissionsSectionDisplayModeMap[data.permissionsSectionDisplayMode],
    memberListDisplayMode: callUnauthorizedMemberListDisplayModeMap[data.memberListDisplayMode],
    showDurationLimitReachedBanner: data.showDurationLimitReachedBanner,
    showCallWillEndSoonBanner: data.showCallWillEndSoonBanner,
    showParticipantsLimitWillReachSoonBanner: data.showParticipantsLimitWillReachSoonBanner,
    showParticipantsRestrictedLimitReachedBanner: data.showParticipantsRestrictedLimitReachedBanner,
  };
};

export const callResolutionResponseMap: Record<VideoResolution, CallVideoResolution> = {
  [VideoResolution.P360]: CallVideoResolution.P360,
  [VideoResolution.P480]: CallVideoResolution.P480,
  [VideoResolution.P720]: CallVideoResolution.P720,
  [VideoResolution.P1080]: CallVideoResolution.P1080,
};

export const mapCallB2bQualitySettings = (
  qualitySettings: VideoQualitySettings
): IVideoQualitySettings | null => {
  if (!qualitySettings.high) {
    return null;
  }

  return {
    low: {
      maxBitrate: qualitySettings.low!.maxBitrate,
      maxFramerate: qualitySettings.low!.maxFps,
      resolution: callResolutionResponseMap[qualitySettings.low!.resolution],
    },
    medium: {
      maxBitrate: qualitySettings.medium!.maxBitrate,
      maxFramerate: qualitySettings.medium!.maxFps,
      resolution: callResolutionResponseMap[qualitySettings.medium!.resolution],
    },
    high: {
      maxBitrate: qualitySettings.high!.maxBitrate,
      maxFramerate: qualitySettings.high!.maxFps,
      resolution: callResolutionResponseMap[qualitySettings.high!.resolution],
    },
  };
};

export const mapCallUnauthorizedInfo = (info: CallInfo): ICallInfo => {
  const { call, initiatorData, selfData } = info;

  const members =
    initiatorData!.memberId === selfData!.member!.memberId
      ? [initiatorData!]
      : [initiatorData!, selfData!.member!];

  return {
    channelId: call!.channelId,
    recordingId: call!.actualRecordingId,
    audioVideoToken: selfData!.cameraToken,
    shareScreenToken: selfData!.screenToken,
    audioVideoUid: selfData!.member!.device!.cameraId,
    shareScreenUid: selfData!.member!.device!.screenId,
    initiatorId: String(initiatorData!.memberId),
    isAdmin: selfData!.member!.isAdmin,
    currentTs: String(Date.now()),
    members: mapCallUnauthorizedMembers(members),
    callVisibleDuration: Number(call!.settings?.callVisibleDurationMs),
    callName: call!.callName || '',
    waitingRoomEnabled: call!.settings!.waitingRoomEnabled,
    guestsAllowed: call!.settings!.guestsAllowed,
    deviceId: selfData!.member!.device!.deviceId!,
    uiFeatures: mapUnauthorizedCallUiFeatures(selfData!.callUiConfiguration!),
    maxBroadcasters: call!.settings!.maxBroadcasters,
    maxParticipants: call!.settings!.maxParticipants,
    maxParticipantsReminder: call!.settings!.maxParticipantsReminder,
    maxDuration: Number(call!.settings!.maxDurationMs),
    maxDurationReminder: Number(call!.settings!.maxDurationReminderMs),
    approxDurationMs: Number(call!.approxDurationMs),
    videoQualitySettings: call!.settings!.videoQualitySettings
      ? mapCallB2bQualitySettings(call!.settings!.videoQualitySettings)
      : null,
  };
};

export const mapUnauthorizedCallStatus = (status: Call_Status): CallStatus => {
  switch (status) {
    case Call_Status.UNANSWERED:
      return CallStatus.Initiated;
    case Call_Status.IN_PROGRESS:
      return CallStatus.Started;
    default:
      return CallStatus.Finished;
  }
};

const mapUnauthorizedStatusCallChangedFinishReasonMap: Record<
  Call_FinishReason,
  StatusCallFinishReason
> = {
  [Call_FinishReason.DURATION_LIMIT_REACHED]: StatusCallFinishReason.DurationLimitReached,
};

export const mapUnauthorizedStatusCallChanged = (
  data: RealtimeCallEvent_CallChanged
): IStatusCallChanged => {
  return {
    waitingRoomEnabled: data.settings!.waitingRoomEnabled,
    guestsAllowed: data.settings!.guestsAllowed,
    status: mapUnauthorizedCallStatus(data.status),
    uiFeatures: mapUnauthorizedCallUiFeatures(data.callUiConfiguration!),
    statusCallFinishReason:
      typeof data.finishReason !== 'undefined'
        ? mapUnauthorizedStatusCallChangedFinishReasonMap[data.finishReason]
        : undefined,
  };
};

export const mapUnauthorizedMemberChanged = (
  data: RealtimeCallEvent_MemberChanged
): ICallMemberChanged => {
  return {
    memberId: data.memberId.toString(),
    status: mapCallUnauthorizedMemberStatus(data.status),
    deviceId: data.deviceId.toString(),
  };
};

export const mapUnauthorizedRecordingStarted = (
  data: RealtimeCallEvent_CallRecordingStarted
): ICallRecordingStarted => {
  return {
    recordingId: data.recordingId,
    recorderId: data.memberId.toString(),
  };
};

export const mapUnauthorizedRecordingStopped = (
  data: RealtimeCallEvent_CallRecordingStopped
): ICallRecordingStopped => {
  return {
    recordingId: data.recordingId,
  };
};

export const mapUnauthorizedMemberHandAction = (
  data: RealtimeCallEvent_MemberRaisedHand | RealtimeCallEvent_MemberPutHandDown
): ICallMemberHandAction => {
  return {
    memberId: data.memberId.toString(),
  };
};

export const mapUnauthorizedMemberForceMuted = (
  data: RealtimeCallEvent_MemberForceMuted
): ICallMemberForceMuted => {
  return {
    initiatorId: data.initiatorId.toString(),
    targetMemberId: data.targetMemberId.toString(),
    deviceId: data.deviceId ? data.deviceId.toString() : null,
  };
};

export const mapUnauthorizedMemberForceFinished = (
  data: RealtimeCallEvent_MemberForceFinished
): ICallMemberForceFinished => {
  return {
    initiatorId: data.initiatorId.toString(),
    targetMemberId: data.targetMemberId.toString(),
    deviceId: data.deviceId ? data.deviceId.toString() : null,
  };
};

export const mapUnauthorizedMemberWasRemoved = (
  data: RealtimeCallEvent_MemberWasRemoved
): ICallMemberWasRemoved => {
  return {
    memberId: data.targetMemberId.toString(),
    initiatorId: data.initiatorId.toString(),
  };
};
export const mapUnauthorizedMemberWasAdded = (
  data: RealtimeCallEvent_MemberWasAdded
): ICallMember => {
  return mapCallUnauthorizedMember(data.member!);
};

export const mapUnauthorizedB2bWaitingRoomMember = (
  data: RealtimeCallEvent_WaitingRoomMemberChanged
): IWaitingRoomMember => {
  return {
    memberId: String(data.memberId),
    displayData: data.displayData!,
    status: callUnauthorizedB2bWaitingRoomMemberStatusResponseMap[data.status],
  };
};

export const mapUnauthorizedB2bWaitingRoomChanged = (
  data: RealtimeCallEvent_WaitingRoomChanged
): IWaitingRoomChanged => {
  return {
    requested: data.requested.map(mapUnauthorizedB2bWaitingRoomMember),
  };
};

export const unauthorizedMemberRoleMap: Record<CallMember_Role, CallMemberRole> = {
  [CallMember_Role.SUPERUSER]: CallMemberRole.Superuser,
  [CallMember_Role.MEMBER]: CallMemberRole.Member,
  [CallMember_Role.GUEST]: CallMemberRole.Guest,
};

export const mapUnauthorizedMemberRoleChanged = (
  data: RealtimeCallEvent_MemberRoleChanged
): IMemberRoleChanged => {
  return {
    userId: String(data.targetMemberId),
    role: unauthorizedMemberRoleMap[data.role],
  };
};
